import { useEffect, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Loading } from '@/components/ui/loading'
import { Input } from '@/components/ui/input'
import { ChevronDown, ChevronRight, MessageSquare, Search, X } from 'lucide-react'
import { useChatPanel } from './ChatContext'

type NavItem = {
  prescription_id: Id<'prescriptions'>
  company_id?: Id<'companies'>
  numero_prescrizione: string
  patient_name: string
  status: string
  created_at: number
  last_message: { body: string; created_at: number; sender_side: 'clinic' | 'lab' } | null
  unread_count: number
}

function useDebouncedValue(value: string, delayMs: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

export function ChatNav() {
  const { selectPrescription } = useChatPanel()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 250)
  const searchArg = debouncedSearch.length >= 2 ? debouncedSearch : undefined

  const nav = useQuery(api.prescriptionChat.listChatNav, {
    search: searchArg,
  })

  // Con ricerca attiva, espandi automaticamente le società con risultati
  useEffect(() => {
    if (!nav || nav.mode !== 'lab' || !searchArg) return
    const next: Record<string, boolean> = {}
    for (const c of nav.companies) {
      if (c.prescriptions.length > 0) next[c.company_id] = true
    }
    setExpanded((prev) => ({ ...prev, ...next }))
  }, [nav, searchArg])

  if (nav === undefined) {
    return (
      <div className="flex h-full flex-col">
        <SearchBar value={searchInput} onChange={setSearchInput} />
        <div className="flex flex-1 items-center justify-center">
          <Loading text="Caricamento chat..." />
        </div>
      </div>
    )
  }

  if (nav.mode === 'lab') {
    const companies = nav.companies.filter(
      (c) => !searchArg || c.prescriptions.length > 0
    )
    return (
      <div className="flex h-full flex-col">
        <div className="border-b px-4 py-3">
          <h2 className="font-semibold text-gray-900">Chat laboratorio</h2>
          <p className="text-xs text-gray-500">Società e relative prescrizioni</p>
        </div>
        <SearchBar value={searchInput} onChange={setSearchInput} />
        <div className="flex-1 overflow-y-auto">
          {companies.length === 0 ? (
            <Empty searching={!!searchArg} />
          ) : (
            companies.map((company) => {
              const open =
                expanded[company.company_id] ??
                (company.unread_total > 0 || !!searchArg)
              return (
                <div key={company.company_id} className="border-b">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-3 text-left hover:bg-gray-50"
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [company.company_id]: !open,
                      }))
                    }
                  >
                    {open ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                    <span className="min-w-0 flex-1 truncate font-medium text-gray-900">
                      {company.company_name}
                    </span>
                    {company.unread_total > 0 && (
                      <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                        {company.unread_total}
                      </span>
                    )}
                  </button>
                  {open && (
                    <div className="bg-gray-50/80 pb-2">
                      {company.prescriptions.length === 0 ? (
                        <p className="px-4 py-2 text-xs text-gray-500">Nessuna prescrizione</p>
                      ) : (
                        company.prescriptions.map((item: NavItem) => (
                          <PrescriptionRow
                            key={item.prescription_id}
                            item={item}
                            onSelect={() => selectPrescription(item.prescription_id)}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-3">
        <h2 className="font-semibold text-gray-900">Chat prescrizioni</h2>
        <p className="text-xs text-gray-500">Scrivi al laboratorio</p>
      </div>
      <SearchBar value={searchInput} onChange={setSearchInput} />
      <div className="flex-1 overflow-y-auto">
        {nav.prescriptions.length === 0 ? (
          <Empty searching={!!searchArg} />
        ) : (
          nav.prescriptions.map((item: NavItem) => (
            <PrescriptionRow
              key={item.prescription_id}
              item={item}
              onSelect={() => selectPrescription(item.prescription_id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function SearchBar({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="border-b px-3 py-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Cerca paziente o n. prescrizione..."
          className="h-9 pl-8 pr-8 text-sm"
        />
        {value && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => onChange('')}
            aria-label="Pulisci ricerca"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

function PrescriptionRow({
  item,
  onSelect,
}: {
  item: NavItem
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-start gap-2 border-b border-transparent px-4 py-2.5 text-left hover:bg-white"
    >
      <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">
            {item.patient_name}
          </span>
          {item.unread_count > 0 && (
            <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {item.unread_count}
            </span>
          )}
        </div>
        <div className="truncate text-xs text-gray-500">
          n. {item.numero_prescrizione}
        </div>
        {item.last_message ? (
          <div className="mt-0.5 truncate text-xs text-gray-600">
            {item.last_message.body || 'Allegato'}
          </div>
        ) : (
          <div className="mt-0.5 text-xs text-gray-400">Nessun messaggio</div>
        )}
      </div>
    </button>
  )
}

function Empty({ searching }: { searching?: boolean }) {
  return (
    <div className="px-4 py-10 text-center text-sm text-gray-500">
      {searching
        ? 'Nessun risultato per la ricerca.'
        : 'Nessuna prescrizione disponibile per la chat.'}
    </div>
  )
}
